import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/Badge';
import { Btn } from '@/components/ui/Button';
import { SectionCard } from '@/components/ui/Card';
import { FieldGrid } from '@/components/ui/FieldGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { substances, makeSpectrum } from '@/data';
import type { Substance } from '@/data/types';

interface SpectrumChartProps {
  substance: Substance;
  overlay: boolean;
}

function SpectrumChart({ substance, overlay }: SpectrumChartProps) {
  const { t } = useTranslation();
  const W = 760, H = 300, padL = 52, padB = 38, padT = 16, padR = 16;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  const pts = useMemo(() => makeSpectrum(substance.peaks, 420), [substance.peaks]);
  const fieldPts = useMemo(() => {
    if (!overlay) return null;
    const shifted = substance.peaks.map(([c, h, w]) => [c + 4, h * 0.82, w * 1.15] as [number, number, number]);
    return makeSpectrum(shifted, 420);
  }, [substance.peaks, overlay]);

  const maxY = Math.max(...pts.map((p) => p[1])) * 1.1;
  const sx = (x: number) => padL + ((x - 200) / 1800) * plotW;
  const sy = (y: number) => padT + plotH - (y / maxY) * plotH;
  const toPath = (arr: [number, number][]) => arr.map((p, i) => (i === 0 ? "M" : "L") + sx(p[0]).toFixed(1) + " " + sy(p[1]).toFixed(1)).join(" ");

  const xticks = [200, 500, 800, 1100, 1400, 1700, 2000];
  const topPeaks = [...substance.peaks].sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <svg viewBox={"0 0 " + W + " " + H} className="mp-spectrum" role="img" aria-label={t('spectrumViewer')}>
      <rect x={padL} y={padT} width={plotW} height={plotH} fill="#FFFFFF" stroke="#E0E5EB"></rect>
      {xticks.map((x) => (
        <line key={x} x1={sx(x)} y1={padT} x2={sx(x)} y2={padT + plotH} stroke="#F1F1F1"></line>
      ))}
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * f} x2={padL + plotW} y2={padT + plotH * f} stroke="#F1F1F1"></line>
      ))}
      {fieldPts ? (
        <path d={toPath(fieldPts)} fill="none" stroke="#ED1B2F" strokeWidth="1.4" opacity="0.75" strokeDasharray="4 3"></path>
      ) : null}
      <path d={toPath(pts)} fill="none" stroke="#0054A6" strokeWidth="1.8"></path>
      {topPeaks.map(([c, h]) => (
        <g key={c}>
          <line x1={sx(c)} y1={sy(h + 0.02)} x2={sx(c)} y2={sy(h + 0.02) - 14} stroke="#9AA5B1" strokeDasharray="2 2"></line>
          <text x={sx(c)} y={sy(h + 0.02) - 18} textAnchor="middle" fontSize="10" fill="#124769" fontWeight="600">{c}</text>
        </g>
      ))}
      {xticks.map((x) => (
        <text key={x} x={sx(x)} y={H - 16} textAnchor="middle" fontSize="10" fill="#707070">{x}</text>
      ))}
      <text x={padL + plotW / 2} y={H - 2} textAnchor="middle" fontSize="10.5" fill="#313131">{t('wavenumber')}</text>
      <text x={14} y={padT + plotH / 2} textAnchor="middle" fontSize="10.5" fill="#313131"
        transform={"rotate(-90 14 " + (padT + plotH / 2) + ")"}>{t('intensity')}</text>
      <g>
        <line x1={padL + 10} y1={padT + 12} x2={padL + 34} y2={padT + 12} stroke="#0054A6" strokeWidth="1.8"></line>
        <text x={padL + 40} y={padT + 16} fontSize="10.5" fill="#313131">{t('refSpectra')}</text>
        {overlay ? (
          <g>
            <line x1={padL + 130} y1={padT + 12} x2={padL + 154} y2={padT + 12} stroke="#ED1B2F" strokeWidth="1.4" strokeDasharray="4 3"></line>
            <text x={padL + 160} y={padT + 16} fontSize="10.5" fill="#313131">{t('fieldSamples')} ({t('matchScore')} 93.4%)</text>
          </g>
        ) : null}
      </g>
    </svg>
  );
}

export default function Substances() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ln = (ko: string, mn: string) => (lang === "ko" ? ko : mn);
  const [selId, setSelId] = useState("meth");
  const [overlay, setOverlay] = useState(true);
  const sub = substances.find((s) => s.id === selId)!;

  return (
    <div className="mp-page" data-screen-label="마약 물질 데이터">
      <PageHeader
        crumbs={[t('home'), t('mDrugData'), t('mSubstance')]}
        title={t('substanceTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('substanceListSub')}</span></div>}
        actions={<Btn kind="primary" icon="bi-plus-lg">{t('register')}</Btn>}
      />

      <div className="mp-grid mp-grid--master-detail">
        <SectionCard flush>
          <ul className="mp-sublist">
            {substances.map((s) => (
              <li key={s.id}>
                <button type="button"
                  className={"mp-sublist__item" + (s.id === selId ? " is-current" : "")}
                  onClick={() => setSelId(s.id)}>
                  <span className="mp-sublist__name">{s.name}</span>
                  <span className="mp-sublist__en">{s.nameEn}</span>
                  <span className="mp-sublist__meta">
                    <span><i className="bi bi-graph-up"></i> {s.refCount}</span>
                    <span><i className="bi bi-folder2"></i> {s.caseCount}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </SectionCard>

        <div className="mp-stack">
          <SectionCard
            title={<span>{sub.name} <span className="mp-sublist__en" style={{ marginLeft: 8 }}>{sub.nameEn}</span></span>}
            extra={<Badge tone="danger">{t('riskHigh')}</Badge>}>
            <FieldGrid rows={[
              [t('casNo'), sub.cas],
              [t('molFormula'), sub.formula],
              [t('classification'), t('scheduleVal')],
              [t('appearance'), ln(sub.appearanceKo, sub.appearanceMn)],
              [t('commonNames'), ln(sub.aliasKo, sub.aliasMn)],
              [t('linkedCases'), sub.caseCount + " " + t('items')],
              [t('refSpectra'), sub.refCount + " " + t('items')],
              [t('fieldSamples'), sub.fieldCount + " " + t('items')],
            ]} />
          </SectionCard>

          <SectionCard
            title={t('spectrumViewer')}
            extra={
              <div className="mp-btnrow">
                <label className="mp-check">
                  <input type="checkbox" checked={overlay} onChange={(e) => setOverlay(e.target.checked)} />
                  <span>{t('overlayField')}</span>
                </label>
                <select className="mp-miniselect" defaultValue="raman">
                  <option value="raman">Raman (785 nm)</option>
                  <option value="ftir">FTIR</option>
                  <option value="gcms">GC-MS</option>
                </select>
              </div>
            }>
            <SpectrumChart substance={sub} overlay={overlay} />
          </SectionCard>

          <SectionCard title={t('refSpectra')} flush>
            <table className="mp-table">
              <thead>
                <tr>
                  <th>{t('refCode')}</th>
                  <th>{t('equipment')}</th>
                  <th>{t('measuredDate')}</th>
                  <th>{t('verifyStatus')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sub.refs.map((r) => (
                  <tr key={r.code}>
                    <td><span className="mp-caseno">{r.code}</span></td>
                    <td>{r.equip}</td>
                    <td>{r.date}</td>
                    <td>
                      {r.status === "verified"
                        ? <Badge tone="success">{t('verified')}</Badge>
                        : <Badge tone="warning">{t('unverified')}</Badge>}
                    </td>
                    <td><button type="button" className="mp-tablelink"><i className="bi bi-graph-up"></i> {t('spectrumViewer')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
